// File: ResellerManagement.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Tooltip,
  Tabs,
  Tab,
  FormHelperText,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

interface Reseller {
  id: number;
  cust_id?: number;
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  company?: string;
  customerCount: number;
  totalExtensions: number;
  totalNumbers: number;
  status: 'active' | 'inactive';
  createdAt: string;
  lastLogin?: string;
}

interface Customer {
  id: number;
  name: string;
  extensionCount: number;
  numberCount: number;
}

const ResellerManagement: React.FC = () => {
  const { member } = useAuth();
  const [resellers, setResellers] = useState<Reseller[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [selectedReseller, setSelectedReseller] = useState<Reseller | null>(null);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    company: '',
    password: '',
    billingModel: 'wholesale_only',
    billingContactName: '',
    billingContactEmail: '',
    billingContactPhone: '',
    platformFeePerCustomer: '1.50',
    revenueSharePercent: '25',
    paymentProcessor: 'moneris',
    monerisStoreId: '',
    monerisApiToken: '',
    emailProvider: 'default',
    mailtrapApiKey: '',
    smtpHost: '',
    smtpPort: '587',
    smtpUsername: '',
    smtpPassword: '',
    smtpEncryption: 'tls',
    fromEmail: '',
    fromName: '',
    hstNumber: '',
    invoiceCompanyName: '',
    invoiceAddress: '',
    invoiceCity: '',
    invoiceProvince: '',
    invoicePostalCode: '',
    invoiceFooterMessage: '',
  });
  const [availableCustomers, setAvailableCustomers] = useState<Customer[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchResellers();
  }, []);

  const fetchResellers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/endpoints/resellers');
      console.log('Resellers API response:', response);
      if (response.data.success) {
        setResellers(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching resellers:', error);
      console.error('Error response:', error.response);
      const errorMessage = error.response?.data?.message || 'Failed to fetch resellers';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = async (reseller?: Reseller) => {
    if (reseller) {
      setSelectedReseller(reseller);
      
      // Fetch full reseller details including config
      try {
        const response = await api.get(`/endpoints/resellers.php?id=${reseller.id}`);
        console.log('Fetching reseller details for ID:', reseller.id);
        console.log('Reseller details response:', response);
        if (response.data.success) {
          const fullData = response.data.data;
          console.log('Full reseller data:', fullData);
          setFormData({
            firstname: fullData.firstname,
            lastname: fullData.lastname,
            email: fullData.email,
            phone: fullData.phone || '',
            company: fullData.company || '',
            password: '',
            billingModel: fullData.billingModel || 'wholesale_only',
            billingContactName: fullData.billingContactName || '',
            billingContactEmail: fullData.billingContactEmail || '',
            billingContactPhone: fullData.billingContactPhone || '',
            platformFeePerCustomer: fullData.platformFeePerCustomer || '1.50',
            revenueSharePercent: fullData.revenueSharePercent || '25',
            paymentProcessor: fullData.paymentProcessor || 'moneris',
            monerisStoreId: fullData.monerisStoreId || '',
            monerisApiToken: fullData.monerisApiToken || '',
            emailProvider: fullData.emailProvider || 'default',
            mailtrapApiKey: fullData.mailtrapApiKey || '',
            smtpHost: fullData.smtpHost || '',
            smtpPort: fullData.smtpPort || '587',
            smtpUsername: fullData.smtpUsername || '',
            smtpPassword: fullData.smtpPassword || '',
            smtpEncryption: fullData.smtpEncryption || 'tls',
            fromEmail: fullData.fromEmail || '',
            fromName: fullData.fromName || '',
            hstNumber: fullData.hstNumber || '',
            invoiceCompanyName: fullData.invoiceCompanyName || '',
            invoiceAddress: fullData.invoiceAddress || '',
            invoiceCity: fullData.invoiceCity || '',
            invoiceProvince: fullData.invoiceProvince || '',
            invoicePostalCode: fullData.invoicePostalCode || '',
            invoiceFooterMessage: fullData.invoiceFooterMessage || '',
          });
        }
      } catch (error) {
        console.error('Error fetching reseller details:', error);
        // Still open dialog with basic data
        setFormData({
          firstname: reseller.firstname,
          lastname: reseller.lastname,
          email: reseller.email,
          phone: reseller.phone || '',
          company: reseller.company || '',
          password: '',
          billingModel: 'wholesale_only',
          billingContactName: '',
          billingContactEmail: '',
          billingContactPhone: '',
          platformFeePerCustomer: '1.50',
          revenueSharePercent: '25',
          paymentProcessor: 'moneris',
          monerisStoreId: '',
          monerisApiToken: '',
          emailProvider: 'default',
          mailtrapApiKey: '',
          smtpHost: '',
          smtpPort: '587',
          smtpUsername: '',
          smtpPassword: '',
          smtpEncryption: 'tls',
          fromEmail: '',
          fromName: '',
          hstNumber: '',
          invoiceCompanyName: '',
          invoiceAddress: '',
          invoiceCity: '',
          invoiceProvince: '',
          invoicePostalCode: '',
          invoiceFooterMessage: '',
        });
      }
    } else {
      setSelectedReseller(null);
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        company: '',
        password: '',
        billingModel: 'wholesale_only',
        billingContactName: '',
        billingContactEmail: '',
        billingContactPhone: '',
        platformFeePerCustomer: '1.50',
        revenueSharePercent: '25',
        paymentProcessor: 'moneris',
        monerisStoreId: '',
        monerisApiToken: '',
        emailProvider: 'mailtrap',
        mailtrapApiKey: '',
        smtpHost: '',
        smtpPort: '587',
        smtpUsername: '',
        smtpPassword: '',
        smtpEncryption: 'tls',
        fromEmail: '',
        fromName: '',
        hstNumber: '',
        invoiceCompanyName: '',
        invoiceAddress: '',
        invoiceCity: '',
        invoiceProvince: '',
        invoicePostalCode: '',
        invoiceFooterMessage: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedReseller(null);
    setTabValue(0);
    setFormData({
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      company: '',
      password: '',
      billingModel: 'wholesale_only',
      billingContactName: '',
      billingContactEmail: '',
      billingContactPhone: '',
      platformFeePerCustomer: '1.50',
      revenueSharePercent: '25',
      paymentProcessor: 'moneris',
      monerisStoreId: '',
      monerisApiToken: '',
      emailProvider: 'default',
      mailtrapApiKey: '',
      smtpHost: '',
      smtpPort: '587',
      smtpUsername: '',
      smtpPassword: '',
      smtpEncryption: 'tls',
      fromEmail: '',
      fromName: '',
      hstNumber: '',
      invoiceCompanyName: '',
      invoiceAddress: '',
      invoiceCity: '',
      invoiceProvince: '',
      invoicePostalCode: '',
      invoiceFooterMessage: '',
    });
  };

  const handleSubmit = async () => {
    try {
      console.log('Submitting form data:', formData);
      if (selectedReseller) {
        // Update existing reseller
        console.log('Updating reseller ID:', selectedReseller.id);
        const response = await api.put(`/endpoints/resellers.php?id=${selectedReseller.id}`, formData);
        if (response.data.success) {
          setSnackbar({ open: true, message: 'Reseller updated successfully', severity: 'success' });
          fetchResellers();
          handleCloseDialog();
        }
      } else {
        // Create new reseller
        const response = await api.post('/endpoints/resellers', formData);
        if (response.data.success) {
          setSnackbar({ open: true, message: 'Reseller created successfully', severity: 'success' });
          fetchResellers();
          handleCloseDialog();
        }
      }
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Operation failed', 
        severity: 'error' 
      });
    }
  };

  const handleDelete = async (resellerId: number) => {
    if (!window.confirm('Are you sure you want to delete this reseller? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await api.delete(`/endpoints/resellers/${resellerId}`);
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Reseller deleted successfully', severity: 'success' });
        fetchResellers();
      }
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to delete reseller', 
        severity: 'error' 
      });
    }
  };

  const handleOpenAssignDialog = async (reseller: Reseller) => {
    setSelectedReseller(reseller);
    try {
      const response = await api.get(`/endpoints/resellers/${reseller.id}/available-customers`);
      if (response.data.success && Array.isArray(response.data.data)) {
        setAvailableCustomers(response.data.data);
      } else {
        setAvailableCustomers([]);
      }
    } catch (error) {
      console.error('Error fetching available customers:', error);
      setSnackbar({ open: true, message: 'Failed to fetch available customers', severity: 'error' });
    }
    setOpenAssignDialog(true);
  };

  const handleAssignCustomers = async () => {
    if (!selectedReseller) return;

    try {
      const response = await api.post(`/endpoints/resellers/${selectedReseller.id}/assign-customers`, {
        customerIds: selectedCustomers
      });
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Customers assigned successfully', severity: 'success' });
        fetchResellers();
        setOpenAssignDialog(false);
        setSelectedCustomers([]);
      }
    } catch (error: any) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to assign customers', 
        severity: 'error' 
      });
    }
  };

  const filteredResellers = resellers.filter(reseller => {
    const matchesSearch = 
      reseller.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reseller.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reseller.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reseller.company && reseller.company.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || reseller.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const paginatedResellers = filteredResellers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Partner Management
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchResellers}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Partner
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search partners..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                label="Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Company</TableCell>
              <TableCell align="center">Customers</TableCell>
              <TableCell align="center">Extensions</TableCell>
              <TableCell align="center">Numbers</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedResellers.map((reseller) => (
              <TableRow key={reseller.id}>
                <TableCell>{reseller.firstname} {reseller.lastname}</TableCell>
                <TableCell>{reseller.email}</TableCell>
                <TableCell>{reseller.company || '-'}</TableCell>
                <TableCell align="center">
                  <Chip
                    icon={<BusinessIcon />}
                    label={reseller.customerCount}
                    size="small"
                    color="primary"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    icon={<PhoneIcon />}
                    label={reseller.totalExtensions}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Chip
                    icon={<PhoneIcon />}
                    label={reseller.totalNumbers}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={reseller.status}
                    color={reseller.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {reseller.lastLogin ? new Date(reseller.lastLogin).toLocaleDateString() : 'Never'}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Assign Customers">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenAssignDialog(reseller)}
                    >
                      <AssignmentIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(reseller)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(reseller.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredResellers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Add/Edit Reseller Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedReseller ? 'Edit Partner' : 'Add New Partner'}
        </DialogTitle>
        <DialogContent>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Basic Info" />
            <Tab label="Billing Info" />
            <Tab label="Email Settings" />
          </Tabs>
          
          {/* Basic Info Tab */}
          {tabValue === 0 && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={formData.firstname}
                    onChange={(e) => setFormData({ ...formData, firstname: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={formData.lastname}
                    onChange={(e) => setFormData({ ...formData, lastname: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  />
                </Grid>
                {!selectedReseller && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
          
          {/* Billing Info Tab */}
          {tabValue === 1 && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Partner Billing Model</InputLabel>
                    <Select
                      value={formData.billingModel}
                      onChange={(e) => setFormData({ ...formData, billingModel: e.target.value })}
                      label="Partner Billing Model"
                    >
                      <MenuItem value="wholesale_only">Wholesale Only - Partner handles all billing</MenuItem>
                      <MenuItem value="managed_billing">Managed Billing - Use our platform (pays platform fee)</MenuItem>
                      <MenuItem value="revenue_share">Revenue Share - We bill, partner gets % commission</MenuItem>
                      <MenuItem value="cost_plus">Cost Plus - We bill, show partner profit margins</MenuItem>
                    </Select>
                    <FormHelperText>
                      {formData.billingModel === 'wholesale_only' && 'Partner handles all customer billing independently'}
                      {formData.billingModel === 'managed_billing' && 'Partner uses our platform but payments go to their processor'}
                      {formData.billingModel === 'revenue_share' && 'We handle billing and share revenue with partner'}
                      {formData.billingModel === 'cost_plus' && 'We handle billing and show partner their profit margins'}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                
                {/* Billing Contact Info */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Billing Contact</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Contact Name"
                    value={formData.billingContactName}
                    onChange={(e) => setFormData({ ...formData, billingContactName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Contact Email"
                    type="email"
                    value={formData.billingContactEmail}
                    onChange={(e) => setFormData({ ...formData, billingContactEmail: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Contact Phone"
                    value={formData.billingContactPhone}
                    onChange={(e) => setFormData({ ...formData, billingContactPhone: e.target.value })}
                  />
                </Grid>
                
                {/* Model-specific settings */}
                {formData.billingModel === 'managed_billing' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }}>Managed Billing Settings</Divider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Platform Fee per Customer"
                        value={formData.platformFeePerCustomer}
                        onChange={(e) => setFormData({ ...formData, platformFeePerCustomer: e.target.value })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        helperText="Monthly fee per active customer"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Payment Processor</InputLabel>
                        <Select
                          value={formData.paymentProcessor}
                          onChange={(e) => setFormData({ ...formData, paymentProcessor: e.target.value })}
                          label="Payment Processor"
                        >
                          <MenuItem value="moneris">Moneris</MenuItem>
                          <MenuItem value="stripe">Stripe</MenuItem>
                          <MenuItem value="square">Square</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {formData.paymentProcessor === 'moneris' && (
                      <>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Moneris Store ID"
                            value={formData.monerisStoreId}
                            onChange={(e) => setFormData({ ...formData, monerisStoreId: e.target.value })}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Moneris API Token"
                            type="password"
                            value={formData.monerisApiToken}
                            onChange={(e) => setFormData({ ...formData, monerisApiToken: e.target.value })}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
                
                {formData.billingModel === 'revenue_share' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }}>Revenue Share Settings</Divider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Revenue Share Percentage"
                        value={formData.revenueSharePercent}
                        onChange={(e) => setFormData({ ...formData, revenueSharePercent: e.target.value })}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        helperText="Percentage of revenue partner receives"
                      />
                    </Grid>
                  </>
                )}
                
                {formData.billingModel === 'cost_plus' && (
                  <>
                    <Grid item xs={12}>
                      <Alert severity="info">
                        In Cost Plus model, we handle all billing and show you the wholesale costs and profit margins for transparency.
                      </Alert>
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>Invoice Details</Divider>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Invoice Company Name"
                    value={formData.invoiceCompanyName}
                    onChange={(e) => setFormData({ ...formData, invoiceCompanyName: e.target.value })}
                    helperText="Company name as it appears on invoices"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="HST/GST Number"
                    value={formData.hstNumber}
                    onChange={(e) => setFormData({ ...formData, hstNumber: e.target.value })}
                    helperText="Tax registration number"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Invoice Address"
                    value={formData.invoiceAddress}
                    onChange={(e) => setFormData({ ...formData, invoiceAddress: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={formData.invoiceCity}
                    onChange={(e) => setFormData({ ...formData, invoiceCity: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Province</InputLabel>
                    <Select
                      value={formData.invoiceProvince}
                      onChange={(e) => setFormData({ ...formData, invoiceProvince: e.target.value })}
                      label="Province"
                    >
                      <MenuItem value="ON">Ontario</MenuItem>
                      <MenuItem value="BC">British Columbia</MenuItem>
                      <MenuItem value="AB">Alberta</MenuItem>
                      <MenuItem value="SK">Saskatchewan</MenuItem>
                      <MenuItem value="MB">Manitoba</MenuItem>
                      <MenuItem value="QC">Quebec</MenuItem>
                      <MenuItem value="NB">New Brunswick</MenuItem>
                      <MenuItem value="NS">Nova Scotia</MenuItem>
                      <MenuItem value="PE">Prince Edward Island</MenuItem>
                      <MenuItem value="NL">Newfoundland and Labrador</MenuItem>
                      <MenuItem value="YT">Yukon</MenuItem>
                      <MenuItem value="NT">Northwest Territories</MenuItem>
                      <MenuItem value="NU">Nunavut</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Postal Code"
                    value={formData.invoicePostalCode}
                    onChange={(e) => setFormData({ ...formData, invoicePostalCode: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Invoice Footer Message"
                    value={formData.invoiceFooterMessage}
                    onChange={(e) => setFormData({ ...formData, invoiceFooterMessage: e.target.value })}
                    multiline
                    rows={3}
                    helperText="Custom message that appears at the bottom of invoices"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
          
          {/* Email Settings Tab */}
          {tabValue === 2 && (
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Email Provider</InputLabel>
                    <Select
                      value={formData.emailProvider}
                      onChange={(e) => setFormData({ ...formData, emailProvider: e.target.value })}
                      label="Email Provider"
                    >
                      {selectedReseller?.cust_id === 1002 && <MenuItem value="default">Default (IA Telecom)</MenuItem>}
                      <MenuItem value="mailtrap">Mailtrap</MenuItem>
                      <MenuItem value="smtp">Custom SMTP</MenuItem>
                    </Select>
                    <FormHelperText>
                      {formData.emailProvider === 'default' && 'Uses IA Telecom email infrastructure'}
                      {formData.emailProvider === 'mailtrap' && 'Use your own Mailtrap account for email delivery'}
                      {formData.emailProvider === 'smtp' && 'Configure your own SMTP server'}
                    </FormHelperText>
                  </FormControl>
                </Grid>
                
                {formData.emailProvider === 'mailtrap' && (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Mailtrap API Key"
                        value={formData.mailtrapApiKey}
                        onChange={(e) => setFormData({ ...formData, mailtrapApiKey: e.target.value })}
                        type={formData.mailtrapApiKey?.startsWith('••••') ? 'text' : 'password'}
                        helperText={formData.mailtrapApiKey?.startsWith('••••') ? 
                          `Current key ending in ${formData.mailtrapApiKey.slice(-4)}. Enter new key to update.` : 
                          'Get from https://mailtrap.io/api-tokens'}
                        placeholder={formData.mailtrapApiKey?.startsWith('••••') ? 
                          'Enter new API key to update' : 
                          'Enter Mailtrap API key'}
                        required={!formData.mailtrapApiKey?.startsWith('••••')}
                      />
                    </Grid>
                  </>
                )}
                
                {formData.emailProvider === 'smtp' && (
                  <>
                    <Grid item xs={12} sm={8}>
                      <TextField
                        fullWidth
                        label="SMTP Host"
                        value={formData.smtpHost}
                        onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                        helperText="e.g., smtp.gmail.com"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="SMTP Port"
                        value={formData.smtpPort}
                        onChange={(e) => setFormData({ ...formData, smtpPort: e.target.value })}
                        helperText="Usually 587 or 465"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="SMTP Username"
                        value={formData.smtpUsername}
                        onChange={(e) => setFormData({ ...formData, smtpUsername: e.target.value })}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="SMTP Password"
                        type="password"
                        value={formData.smtpPassword}
                        onChange={(e) => setFormData({ ...formData, smtpPassword: e.target.value })}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Encryption</InputLabel>
                        <Select
                          value={formData.smtpEncryption}
                          onChange={(e) => setFormData({ ...formData, smtpEncryption: e.target.value })}
                          label="Encryption"
                        >
                          <MenuItem value="tls">TLS</MenuItem>
                          <MenuItem value="ssl">SSL</MenuItem>
                          <MenuItem value="none">None</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}
                
                {formData.emailProvider !== 'default' && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="From Email"
                        type="email"
                        value={formData.fromEmail}
                        onChange={(e) => setFormData({ ...formData, fromEmail: e.target.value })}
                        helperText="e.g., billing@yourdomain.com"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="From Name"
                        value={formData.fromName}
                        onChange={(e) => setFormData({ ...formData, fromName: e.target.value })}
                        helperText="e.g., Your Company Billing"
                        required
                      />
                    </Grid>
                  </>
                )}
                
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedReseller ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Customers Dialog */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Assign Customers to Partner: {selectedReseller?.firstname} {selectedReseller?.lastname}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select customers to assign to this partner. Only unassigned customers are shown.
          </Alert>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">Select</TableCell>
                  <TableCell>Customer Name</TableCell>
                  <TableCell align="center">Extensions</TableCell>
                  <TableCell align="center">Numbers</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availableCustomers.map((customer) => (
                  <TableRow key={customer.id}>
                    <TableCell padding="checkbox">
                      <input
                        type="checkbox"
                        checked={selectedCustomers.includes(customer.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCustomers([...selectedCustomers, customer.id]);
                          } else {
                            setSelectedCustomers(selectedCustomers.filter(id => id !== customer.id));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>{customer.name}</TableCell>
                    <TableCell align="center">{customer.extensionCount}</TableCell>
                    <TableCell align="center">{customer.numberCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAssignCustomers} 
            variant="contained" 
            color="primary"
            disabled={selectedCustomers.length === 0}
          >
            Assign Selected ({selectedCustomers.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ResellerManagement;